import { useState, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Eyebrow, Reveal, MaskedLine, Cta, Placeholder, SectionHead } from "@/components/site/ui";
import { ContactSection } from "@/components/site/ContactForm";
import { FOUNDERS_PHOTO, VISION_PHOTO } from "@/lib/assets";
import { LINKS } from "@/lib/links";

const BADGES = ["LIFE COACHING CRISTIANO", "METODO TEMPIO VIVO", "PERCORSI UOMO & DONNA", "APP"];

const AREE = ["FEDE", "MENTE", "CORPO", "EMOZIONI", "RELAZIONI", "CHIAMATA"];

const COSA_FACCIAMO = [
  { t: "Fede", d: "rapporto con Dio, identità, disciplina spirituale, coerenza tra ciò che credi e ciò che vivi." },
  { t: "Mente ed emozioni", d: "paure, ferite, autostima, bisogno di approvazione, gestione emotiva e schemi che bloccano." },
  { t: "Corpo", d: "allenamento, alimentazione, disciplina, rapporto con il proprio corpo e cura di sé." },
  { t: "Relazioni", d: "coppia, matrimonio, famiglia, comunicazione, confini e responsabilità." },
  { t: "Abitudini e disciplina", d: "routine, procrastinazione, dominio di sé, costanza e responsabilità personale." },
  { t: "Dipendenze", d: "lussuria, alcool, pornografia o altro: schemi e legami da cui tornare liberi." },
  { t: "Chiamata e direzione", d: "talenti, lavoro, voce, leadership, scopo e costruzione del proprio futuro." },
];

const COME = [
  { t: "Coaching 1:1", d: "Percorsi personalizzati con Elio o Chiara." },
  { t: "Metodo Tempio Vivo", d: "Il nostro metodo che integra fede, mente, corpo, emozioni, relazioni e chiamata." },
  { t: "Percorsi Uomo e Donna", d: "Uomo Biblico e Donna Virtuosa, con un lavoro specifico sull'identità maschile e femminile." },
  { t: "App Tempio Vivo", d: "Routine quotidiana, percorsi, journaling, allenamenti, community e strumenti pratici." },
  { t: "Call e Community", d: "Momenti di confronto, studio, crescita e supporto." },
];

const FFI_AREAS = ["COACHING", "PERCORSI", "APP", "COMMUNITY", "EVENTI", "ABBIGLIAMENTO"];

const CARDS = [
  {
    title: "MAPPA",
    desc: "Il nostro Metodo di Life Coaching Cristiano per rimettere Dio al centro e lavorare su fede, mente, corpo, emozioni, relazioni, disciplina e chiamata.",
    cta: "SCOPRI LA MAPPA",
    href: LINKS.TEMPIO_VIVO,
    id: "mappa",
    n: "01",
  },
  {
    title: "SFIDA DEL DESERTO",
    desc: "40 giorni. Un primo passo. Il percorso gratuito per iniziare a rimettere ordine nella tua vita con Dio al centro.",
    cta: "INIZIA IL DESERTO",
    href: LINKS.DESERTO,
    id: "deserto",
    n: "02",
  },
  {
    title: "TEMPIO",
    desc: "Lo spazio quotidiano di Tempio Vivo: routine, percorsi, allenamento, journaling, community e contenuti pratici.",
    cta: "SCOPRI TEMPIO",
    href: LINKS.TEMPIO_APP,
    id: "tempio",
    n: "03",
  },
  {
    title: "DONNA VIRTUOSA",
    desc: "Un percorso di crescita femminile cristiana per riscoprire identità, valore e femminilità, lavorando su cuore, emozioni, corpo, relazioni, famiglia e chiamata con Dio al centro.",
    cta: "SCOPRI DONNA VIRTUOSA",
    href: LINKS.DONNA_VIRTUOSA,
    id: "donna",
    n: "04",
  },
  {
    title: "UOMO BIBLICO",
    desc: "Un percorso di crescita maschile cristiana che accompagna l'uomo dal cuore di Davide alla sapienza di Salomone: identità, disciplina, corpo, relazioni, leadership, chiamata e capacità di provvedere con responsabilità.",
    cta: "SCOPRI UOMO BIBLICO",
    href: LINKS.UOMO_BIBLICO,
    id: "uomo",
    n: "05",
  },
];

const STORIA_INTRO = [
  "Prima di diventare Life Coach cristiani, siamo stati semplicemente Elio e Chiara: due persone con storie diverse, ferite diverse e la stessa ricerca di una vita che avesse davvero un senso.",
  "Abbiamo attraversato cambiamenti radicali, cadute, perdite, trasformazioni fisiche, trasferimenti e momenti in cui abbiamo dovuto ricominciare da capo.",
];

const STORIA_FULL = [
  "Elio ha lasciato una vita che sembrava già scritta: il lavoro in Polizia, l'Italia e molte delle sicurezze che conosceva, alla ricerca di libertà, direzione e di qualcosa che sentiva ancora mancare.",
  "Chiara ha attraversato una delle prove più dolorose che una donna possa vivere: la perdita di una figlia durante la gravidanza. Un dolore che non si cancella e che ha segnato profondamente il suo modo di guardare alla vita, alla fede, al corpo, alla famiglia e al significato stesso della parola ricostruzione.",
  "Abbiamo cercato risposte nel cambiamento, nel lavoro, nel fitness, nei risultati e nella possibilità di costruirci una vita diversa. Ma abbiamo scoperto che puoi cambiare città, corpo, lavoro e abitudini e continuare ad avere qualcosa dentro che chiede ordine.",
  "Quando Cristo è diventato davvero il centro, non sono scomparse le difficoltà. È cambiato il modo in cui abbiamo iniziato ad attraversarle.",
  "Da lì abbiamo cominciato a costruire diversamente: la nostra fede, il matrimonio, il rapporto con il corpo, la disciplina, le relazioni, il lavoro e la nostra chiamata.",
  "Ed è proprio da quello che abbiamo vissuto che nasce oggi il nostro modo di accompagnare le persone.",
  "Non parliamo di ricostruzione perché l'abbiamo studiata soltanto. Parliamo di ricostruzione perché, in modi diversi, abbiamo dovuto attraversarla.",
  "Oggi mettiamo la nostra esperienza, la nostra formazione e la nostra storia al servizio di uomini e donne che vogliono rimettere ordine nella propria vita con Dio al centro: nella fede, nella mente, nel corpo, nelle emozioni, nelle relazioni e nella direzione che stanno dando al proprio futuro.",
];

export default function Home() {
  const [storiaOpen, setStoriaOpen] = useState(false);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative grain bg-[#0B0B0B] overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
              <Eyebrow testId="hero-label">FEDE & FITNESS ITALIA</Eyebrow>
            </motion.div>
            <h1 data-testid="hero-headline" className="font-display font-medium tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              <MaskedLine delay={0.1}>RICOSTRUISCI</MaskedLine>
              <MaskedLine delay={0.22}>LA TUA VITA.</MaskedLine>
              <MaskedLine delay={0.34} className="text-[#C9A75D]">CON DIO AL CENTRO.</MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.8 }}
              className="mt-7 text-[#D8C7AA] text-base sm:text-lg tracking-wide"
            >
              Fede. Mente. Corpo. Emozioni. Relazioni.
              <br />
              Dipendenze.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              className="mt-6 text-[#F7F4EE]/75 leading-relaxed max-w-xl"
            >
              Siamo Elio e Chiara, life coach cristiani e fondatori di Fede & Fitness Italia. Abbiamo creato il Metodo
              Tempio Vivo per accompagnare uomini e donne in un percorso concreto di crescita con valori cristiani, in
              base al tuo stato di partenza e a cosa è più urgente risolvere per te.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.8 }}
              className="mt-9 flex flex-col sm:flex-row gap-4"
            >
              <Cta href="#ecosistema" variant="solid-light" testId="hero-cta-primary">
                SCOPRI COSA ABBIAMO CREATO
              </Cta>
              <Cta href={LINKS.APP} variant="gold" testId="hero-cta-secondary">
                SCARICA L'APP
              </Cta>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 1 }}
            className="relative"
          >
            <motion.div style={{ y: imgY }} className="relative overflow-hidden">
              <div className="absolute inset-3 border border-[#C9A75D]/40 z-10 pointer-events-none" />
              <img
                data-testid="hero-founders-photo"
                src={FOUNDERS_PHOTO}
                alt="Elio e Chiara, fondatori di Fede & Fitness Italia"
                className="w-full aspect-[4/5] sm:aspect-[5/5] lg:aspect-[4/5] object-cover"
              />
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#0B0B0B]/80 to-transparent p-6 pt-16">
                <p className="font-display text-lg text-[#F7F4EE]">Elio & Chiara</p>
                <p className="text-[0.65rem] uppercase tracking-[0.25em] text-[#C9A75D] mt-1">
                  Fondatori · Life Coach Cristiani
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* BADGE STRIP */}
      <section data-testid="badge-strip" className="border-y border-[#262626] bg-[#0B0B0B]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 flex flex-wrap justify-center gap-x-10 gap-y-3">
          {BADGES.map((b, i) => (
            <Reveal key={b} delay={i * 0.06}>
              <span className="text-[0.65rem] uppercase tracking-[0.25em] text-[#D8C7AA]/70 flex items-center gap-3">
                <span className="text-[#C9A75D]">✦</span> {b}
              </span>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CHI SIAMO */}
      <section id="chi-siamo" data-testid="chi-siamo-section" className="bg-[#F7F4EE] text-[#0B0B0B]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32 grid lg:grid-cols-5 gap-12 lg:gap-16">
          <Reveal className="lg:col-span-2">
            <div className="lg:sticky lg:top-28">
              <div className="relative overflow-hidden">
                <div className="absolute inset-3 border border-[#C9A75D]/50 z-10 pointer-events-none" />
                <img src={FOUNDERS_PHOTO} alt="Elio e Chiara" className="w-full aspect-[4/5] object-cover" />
              </div>
            </div>
          </Reveal>
          <div className="lg:col-span-3">
            <SectionHead light eyebrow="CHI SIAMO" title={"DUE STORIE DIVERSE.\nUNA STESSA CHIAMATA."} testId="chi-siamo-eyebrow" />
            <div className="mt-8 space-y-5 text-[#0B0B0B]/80 leading-relaxed">
              {STORIA_INTRO.map((p) => (
                <p key={p.slice(0, 30)}>{p}</p>
              ))}
              {storiaOpen && STORIA_FULL.map((p) => <p key={p.slice(0, 30)}>{p}</p>)}
            </div>
            <blockquote className="mt-8 border-l-2 border-[#C9A75D] pl-6 font-display text-xl sm:text-2xl text-[#0B0B0B] leading-snug">
              Abbiamo conosciuto cosa significa perdere, cambiare e ricominciare.
              <br />
              Oggi aiutiamo altre persone a costruire con fondamenta diverse.
            </blockquote>
            {!storiaOpen && (
              <div className="mt-9">
                <Cta variant="solid-dark" testId="chi-siamo-cta" onClick={() => setStoriaOpen(true)}>
                  SCOPRI LA NOSTRA STORIA
                </Cta>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* COSA FACCIAMO */}
      <section data-testid="cosa-facciamo-section" className="bg-[#0B0B0B] grain relative">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32">
          <SectionHead eyebrow="COSA FACCIAMO" title={"LIFE COACHING\nCRISTIANO."} testId="cosa-facciamo-eyebrow" />
          <Reveal delay={0.1}>
            <p className="mt-7 max-w-2xl text-[#F7F4EE]/75 leading-relaxed">
              Accompagniamo uomini e donne in percorsi di crescita personale con Dio al centro. Lavoriamo in modo
              pratico su:
            </p>
          </Reveal>

          {/* COS'È UN LIFE COACH CRISTIANO */}
          <Reveal className="mt-16 border border-[#262626] p-8 sm:p-10" >
            <div data-testid="life-coach-section" className="grid lg:grid-cols-2 gap-10">
              <div>
                <p className="text-[0.68rem] uppercase tracking-[0.3em] text-[#C9A75D] mb-5">COS'È UN LIFE COACH CRISTIANO?</p>
                <h3 className="font-display text-xl sm:text-2xl text-[#F7F4EE] leading-snug">
                  UNA FIGURA DIVERSA
                  <br />
                  DA TUTTE LE ALTRE.
                </h3>
                <p className="mt-5 text-sm text-[#F7F4EE]/75 leading-relaxed">
                  Un Life Coach Cristiano ti accompagna, in modo pratico e concreto, a crescere e a raggiungere
                  obiettivi reali mettendo Dio al centro del percorso. Unisce gli strumenti del coaching alla visione
                  biblica della persona: non è una figura religiosa e non sostituisce percorsi medici o psicologici.
                </p>
              </div>
              <div className="space-y-5">
                {[
                  { t: "Psicologo / Terapeuta", d: "cura e tratta disturbi e patologie. Il life coach cristiano non fa diagnosi né terapia: lavora su crescita, obiettivi e direzione." },
                  { t: "Pastore / Guida spirituale", d: "guida una comunità e insegna la Parola. Il life coach cristiano affianca la tua vita quotidiana con un percorso personale e pratico." },
                  { t: "Personal trainer", d: "lavora solo sul corpo. Il life coach cristiano integra corpo, mente, fede e relazioni nello stesso cammino." },
                  { t: "Coach motivazionale", d: "spinge sui risultati e sull'emozione del momento. Il life coach cristiano costruisce fondamenta durature con Dio al centro." },
                ].map((x) => (
                  <div key={x.t} className="border-t border-[#262626] pt-4">
                    <p className="text-sm font-semibold text-[#D8C7AA] uppercase tracking-wider">{x.t}</p>
                    <p className="mt-1.5 text-sm text-[#F7F4EE]/65 leading-relaxed">{x.d}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#262626]">
            {COSA_FACCIAMO.map((a, i) => (
              <Reveal key={a.t} delay={i * 0.06} className="bg-[#0B0B0B] p-7 hover:bg-[#161616] transition-colors">
                <p className="font-display text-lg text-[#F7F4EE]">{a.t}</p>
                <p className="mt-3 text-sm text-[#D8C7AA]/70 leading-relaxed">{a.d}</p>
              </Reveal>
            ))}
          </div>

          {/* 6 aree */}
          <div className="mt-20 flex flex-wrap justify-center gap-3 sm:gap-4" data-testid="sei-aree">
            {AREE.map((a, i) => (
              <Reveal key={a} delay={i * 0.07}>
                <span className="inline-block border border-[#C9A75D]/40 text-[#C9A75D] px-5 py-2.5 text-[0.7rem] uppercase tracking-[0.25em] hover:bg-[#C9A75D]/10 transition-colors">
                  {a}
                </span>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-20">
            <p className="text-[0.68rem] uppercase tracking-[0.3em] text-[#C9A75D] mb-8">Come lo facciamo</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8">
              {COME.map((c) => (
                <div key={c.t} className="border-t border-[#262626] pt-5">
                  <p className="font-semibold text-sm text-[#F7F4EE] uppercase tracking-wider">{c.t}</p>
                  <p className="mt-2 text-sm text-[#D8C7AA]/70 leading-relaxed">{c.d}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal className="mt-16 flex flex-col sm:flex-row items-start sm:items-center gap-8 justify-between border border-[#262626] p-8">
            <p className="font-display text-lg sm:text-xl text-[#F7F4EE] max-w-xl leading-snug">
              Non lavoriamo soltanto su quello che vuoi ottenere.{" "}
              <span className="text-[#C9A75D]">Lavoriamo sulla persona che devi diventare</span> per costruirlo con Dio
              al centro.
            </p>
            <Cta href={LINKS.TEMPIO_VIVO} variant="gold" testId="cosa-facciamo-cta">
              SCOPRI IL METODO TEMPIO VIVO
            </Cta>
          </Reveal>
        </div>
      </section>

      {/* COS'È FFI */}
      <section data-testid="cose-ffi-section" className="bg-[#262626]/40 border-y border-[#262626]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32">
          <SectionHead eyebrow="COS'È" title="FEDE & FITNESS ITALIA" testId="cose-ffi-eyebrow" />
          <div className="mt-10 grid lg:grid-cols-2 gap-12">
            <Reveal className="space-y-5 text-[#F7F4EE]/75 leading-relaxed">
              <p>
                Fede & Fitness Italia è l'ecosistema che abbiamo creato per aiutare uomini e donne a crescere con Dio al
                centro della propria vita. Nasce dall'unione tra fede cristiana, crescita personale, disciplina, cura
                del corpo, relazioni e chiamata.
              </p>
              <p className="font-display text-lg text-[#F7F4EE]">
                Non è soltanto fitness. Non è soltanto coaching.
                <br />
                Non è soltanto un'app. Non è soltanto una community.
              </p>
              <p>
                È un progetto che raccoglie strumenti, percorsi e ambienti diversi per accompagnare la persona nella sua
                interezza.
              </p>
            </Reveal>
            <Reveal delay={0.12} className="space-y-4 text-sm text-[#D8C7AA]/80 leading-relaxed">
              <p className="text-[0.68rem] uppercase tracking-[0.3em] text-[#C9A75D]">Dentro Fede & Fitness Italia abbiamo creato</p>
              <ul className="space-y-3">
                <li><span className="text-[#F7F4EE] font-medium">Tempio Vivo</span> — il nostro metodo di Life Coaching Cristiano.</li>
                <li><span className="text-[#F7F4EE] font-medium">Uomo Biblico</span> — il percorso dedicato alla crescita maschile.</li>
                <li><span className="text-[#F7F4EE] font-medium">Donna Virtuosa</span> — il percorso dedicato alla crescita femminile.</li>
                <li><span className="text-[#F7F4EE] font-medium">App Tempio Vivo</span> — routine, percorsi, journaling, allenamento e community nella vita quotidiana.</li>
                <li><span className="text-[#F7F4EE] font-medium">Eventi, Connect Group e community</span> — per portare la crescita anche fuori dallo schermo.</li>
                <li><span className="text-[#F7F4EE] font-medium">Abbigliamento Fede & Fitness Italia</span> — prodotti che rappresentano e comunicano i valori del progetto.</li>
              </ul>
            </Reveal>
          </div>

          <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-[#262626]" data-testid="ffi-aree-grid">
            {FFI_AREAS.map((a, i) => (
              <Reveal key={a} delay={i * 0.05} className="bg-[#0B0B0B] py-8 px-4 text-center hover:bg-[#161616] transition-colors">
                <span className="text-[0.65rem] uppercase tracking-[0.22em] text-[#D8C7AA]">{a}</span>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-14 space-y-4 max-w-3xl">
            <p className="text-[#F7F4EE]/75 leading-relaxed">
              Fede & Fitness Italia nasce per unire ciò che spesso viene separato: fede e vita quotidiana. Non vogliamo
              aiutarti a migliorare una sola area della tua vita. Vogliamo aiutarti a costruire una vita intera con Dio
              al centro.
            </p>
          </Reveal>

          <Reveal className="mt-20">
            <p
              data-testid="manifesto-quote"
              className="font-display text-2xl sm:text-4xl lg:text-5xl leading-[1.15] text-center max-w-4xl mx-auto"
            >
              LA FEDE NON È UNA PARTE DELLA VITA.
              <br />
              <span className="text-[#C9A75D]">È IL CENTRO DA CUI COSTRUIRE TUTTO IL RESTO.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* ECOSISTEMA — 5 CARDS */}
      <section id="ecosistema" data-testid="ecosistema-section" className="bg-[#0B0B0B]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32">
          <SectionHead eyebrow="IL NOSTRO ECOSISTEMA" title="COSA ABBIAMO CREATO." testId="ecosistema-eyebrow" />
          <div className="mt-14 space-y-px bg-[#262626]">
            {CARDS.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.05}>
                <a
                  href={c.href}
                  {...(c.href.startsWith("http") ? { target: "_blank", rel: "noreferrer" } : {})}
                  data-testid={`ecosystem-card-${c.id}`}
                  className="group bg-[#0B0B0B] hover:bg-[#161616] transition-colors grid sm:grid-cols-12 gap-4 sm:gap-8 items-center px-6 sm:px-10 py-10 block"
                >
                  <span className="sm:col-span-1 font-display text-[#C9A75D]/60 text-lg">{c.n}</span>
                  <h3 className="sm:col-span-3 font-display text-2xl lg:text-3xl text-[#F7F4EE] group-hover:text-[#C9A75D] transition-colors">
                    {c.title}
                  </h3>
                  <p className="sm:col-span-6 text-sm text-[#D8C7AA]/70 leading-relaxed">{c.desc}</p>
                  <span className="sm:col-span-2 text-[0.65rem] uppercase tracking-[0.2em] text-[#C9A75D] flex items-center gap-2 sm:justify-end">
                    {c.cta}
                    <span className="transition-transform group-hover:translate-x-1">→</span>
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VISIONE */}
      <section data-testid="visione-section" className="relative overflow-hidden">
        <img src={VISION_PHOTO} alt="Visione" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-[#0B0B0B]/70" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-28 lg:py-40 text-center flex flex-col items-center">
          <SectionHead center eyebrow="LA NOSTRA VISIONE" title="" testId="visione-eyebrow" />
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-[#F7F4EE]">
              NON VOGLIAMO SOLO ISPIRARE PERSONE.
              <br />
              VOGLIAMO VEDERE VITE TRASFORMATE.
            </h2>
            <p className="mt-6 max-w-2xl mx-auto text-[#F7F4EE]/70 leading-relaxed">
              La nostra visione è costruire un punto di riferimento per il Life Coaching Cristiano e la Crescita
              Integrata, dove la fede non rimanga soltanto qualcosa da professare, ma diventi qualcosa da vivere ogni
              giorno.
            </p>
            <p className="mt-4 max-w-2xl mx-auto text-[#F7F4EE]/70 leading-relaxed">
              Attraverso il Metodo Tempio Vivo, vogliamo accompagnare uomini, donne e famiglie a crescere nella fede,
              rinnovare la mente, prendersi cura del corpo, sviluppare disciplina, costruire relazioni sane e trovare
              una direzione nella propria vita.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="mt-14">
            <p className="font-display text-xl sm:text-3xl text-[#C9A75D] max-w-3xl mx-auto">
              NON STIAMO COSTRUENDO SOLTANTO UNA PIATTAFORMA.
              <br />
              STIAMO COSTRUENDO UN LUOGO IN CUI LA FEDE DIVENTA AZIONE.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ROADMAP */}
      <section data-testid="roadmap-section" className="bg-[#F7F4EE] text-[#0B0B0B]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32">
          <SectionHead light eyebrow="ROADMAP" title="DOVE STIAMO ANDANDO." testId="roadmap-eyebrow" />
          <div className="mt-16 grid md:grid-cols-3 gap-10 relative">
            <div className="hidden md:block absolute top-5 left-0 right-0 h-px bg-[#C9A75D]/40" />
            {[
              {
                fase: "FASE 1",
                title: "LE FONDAMENTA",
                items: ["Fede & Fitness Italia", "Primi contenuti", "Primi percorsi", "Community"],
              },
              {
                fase: "FASE 2",
                title: "LA COSTRUZIONE",
                items: ["Metodo Tempio Vivo", "Uomo Biblico", "Donna Virtuosa", "App Tempio Vivo", "Sfida del Deserto", "Connect Group"],
              },
              {
                fase: "FASE 3",
                title: "L'ESPANSIONE",
                future: true,
                items: [
                  "Rete di Life Coach Cristiani formati nel Metodo Tempio Vivo",
                  "Espansione della community in Italia e all'estero",
                  "Connect Group territoriali e incontri dal vivo",
                  "Retreat ed esperienze immersive",
                  "Academy e formazione di nuovi leader",
                  "Collaborazioni con professionisti e realtà cristiane",
                  "Nuovi strumenti digitali per la crescita personale",
                ],
              },
            ].map((f, i) => (
              <Reveal key={f.fase} delay={i * 0.12} className="relative">
                <div className="w-10 h-10 border border-[#C9A75D] bg-[#F7F4EE] flex items-center justify-center text-[#C9A75D] font-display text-sm relative z-10">
                  {i + 1}
                </div>
                <p className="mt-6 text-[0.65rem] uppercase tracking-[0.3em] text-[#8a6f35]">
                  {f.fase}
                  {f.future && <span className="ml-3 border border-[#C9A75D]/60 text-[#8a6f35] px-2 py-0.5 tracking-[0.2em]">IN ARRIVO</span>}
                </p>
                <h3 className="mt-2 font-display text-2xl">{f.title}</h3>
                <ul className="mt-5 space-y-2.5">
                  {f.items.map((it) => (
                    <li key={it} className={`text-sm flex items-start gap-2.5 ${f.future ? "text-[#0B0B0B]/55 italic" : "text-[#0B0B0B]/70"}`}>
                      <span className={`w-1 h-1 mt-2 shrink-0 ${f.future ? "border border-[#C9A75D] bg-transparent" : "bg-[#C9A75D]"}`} /> {it}
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINALE */}
      <section data-testid="cta-finale-section" className="bg-[#0B0B0B] grain relative">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-24 lg:py-32 text-center">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#F7F4EE]">DA DOVE VUOI INIZIARE?</h2>
            <p className="mt-5 text-[#D8C7AA]/80">Scegli il tuo primo passo.</p>
          </Reveal>
          <Reveal delay={0.15} className="mt-12 grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <Cta href={LINKS.TEMPIO_VIVO} variant="solid-light" testId="final-cta-tempio">SCOPRI TEMPIO VIVO</Cta>
            <Cta href={LINKS.APP} variant="gold-fill" testId="final-cta-app">SCARICA L'APP</Cta>
            <Cta href={LINKS.UOMO_BIBLICO} variant="gold" testId="final-cta-uomo">SCOPRI UOMO BIBLICO</Cta>
            <Cta href={LINKS.DONNA_VIRTUOSA} variant="gold" testId="final-cta-donna">SCOPRI DONNA VIRTUOSA</Cta>
          </Reveal>
        </div>
      </section>

      <ContactSection />
    </div>
  );
}
